/**
 * The human decision on a run: go or no-go, with a note on why. One review per run; saving
 * again replaces it.
 */
import { useState } from 'react';
import Button from '../../../../components/Button';
import InlineErrorDisplay from '../../../../components/InlineErrorDisplay';
import type { EvalReview } from '../../types/evalTypes';
import { formatDateTime } from '../../utils/evalFormat';

const MAX_NOTE_CHARS = 2000;

interface ReviewPanelProps {
  review: EvalReview | null;
  currentUid: string | undefined;
  onSave: (decision: EvalReview['decision'], note: string) => Promise<void>;
}

export const ReviewPanel = ({ review, currentUid, onSave }: ReviewPanelProps) => {
  const [editing, setEditing] = useState(review === null);
  const [decision, setDecision] = useState<EvalReview['decision'] | null>(review?.decision ?? null);
  const [note, setNote] = useState(review?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!decision) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(decision, note.trim());
      setEditing(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to save the review.');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDecision(review?.decision ?? null);
    setNote(review?.note ?? '');
    setError(null);
    setEditing(false);
  };

  if (review && !editing) {
    return (
      <div className={`rounded-lg shadow-sm p-4 border ${review.decision === 'go' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Review: {review.decision === 'go' ? 'Go' : 'No-go'}
            </h2>
            <p className="text-xs text-gray-500">
              By {review.reviewedBy === currentUid ? 'you' : review.reviewedBy}, {formatDateTime(review.reviewedAt)}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            Change
          </Button>
        </div>
        {review.note && <p className="text-sm text-gray-800 whitespace-pre-wrap mt-2">{review.note}</p>}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Review</h2>
        <p className="text-sm text-gray-500">Decide after reading the outputs, not only the pass rates. Say why in the note.</p>
      </div>
      <div className="flex gap-2">
        <Button variant={decision === 'go' ? 'success' : 'outline'} size="sm" onClick={() => setDecision('go')} disabled={saving}>
          Go
        </Button>
        <Button variant={decision === 'no-go' ? 'danger' : 'outline'} size="sm" onClick={() => setDecision('no-go')} disabled={saving}>
          No-go
        </Button>
      </div>
      <textarea
        value={note}
        maxLength={MAX_NOTE_CHARS}
        onChange={(event) => setNote(event.target.value)}
        disabled={saving}
        rows={3}
        placeholder="Why? For example: Haiku 5.5 low keeps every risk fact and adds nothing; ship it."
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-y"
      />
      {error && <InlineErrorDisplay message={error} onDismiss={() => setError(null)} />}
      <div className="flex justify-end gap-2">
        {review && (
          <Button variant="outline" size="sm" onClick={cancel} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button variant="primary" size="sm" onClick={() => void save()} disabled={!decision || saving} loading={saving}>
          Save review
        </Button>
      </div>
    </div>
  );
};
